import { Chip } from "@heroui/react";
import { useTranslation } from "react-i18next";
import DateTime from "/app/src/components/generic/formatting/dateTime";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";
import { Report } from "/app/src/models";

/**
 * Date-related values used by VarianceChips.
 *
 * - cache_expiration: next scheduled refresh time.
 * - powerpick_timestamp: last PowerPick data timestamp.
 * - variance_timestamp: last Variance data timestamp.
 */
type DateValues = {
  cache_expiration: string | null;
  powerpick_timestamp: string | null;
  variance_timestamp: string | null;
};

/**
 * VarianceChips
 *
 * Renders a compact row of chips for Variance reports:
 * - Cached data timestamp (picks the most recent between Variance and PowerPick).
 * - A generic disconnected-connections warning if any related connections are disconnected.
 * - The next refresh time.
 *
 * Intended placement: directly below the snapshot chart and above the data table
 * in the report view. Returns null when the report is not a Variance report.
 *
 * @param report The current report payload (expects `isVariance` and `hasDisconnectedConnections`).
 * @param dateValues Cache expiration and last data timestamps used for chip display.
 * @returns A JSX element with chips, or null if not a variance report.
 */
export default function VarianceChips({
  report,
  dateValues,
}: {
  report: Report;
  dateValues: DateValues;
}) {
  const { t } = useTranslation();

  if (!report?.isVariance) return null;

  const cachedDate =
    (dateValues?.variance_timestamp ?? "") >
    (dateValues?.powerpick_timestamp ?? "")
      ? dateValues?.variance_timestamp
      : dateValues?.powerpick_timestamp;

  return (
    <div className="my-2 flex justify-between">
      <Chip
        startContent={<IconBuilder icon="Calendar" size={16} color="#ffffff" />}
        className="p-2"
        variant="solid"
        size="sm"
        color="primary"
      >
        {t("translation:data_cached")}
        <DateTime includeSeconds date={cachedDate} />
      </Chip>
      {report?.hasDisconnectedConnections ? (
        <Chip className="p-2" variant="solid" size="sm" color="warning">
          {t(
            "translation:variance_connections_disconnected",
            "One or more related connections are disconnected.",
          )}
        </Chip>
      ) : null}
      <Chip
        startContent={<IconBuilder icon="Refresh" size={16} color="#ffffff" />}
        className="p-2 mr-2 bg-secondary-default"
        variant="solid"
        size="sm"
        color="primary"
      >
        {t("translation:next_refresh")}
        <DateTime includeSeconds date={dateValues?.cache_expiration} />
      </Chip>
    </div>
  );
}
